import { Button, Card, Descriptions, Table, Tag, message, Divider } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ArrowLeftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import LoadingBox from "../../Components/LoadingBox";
import { API } from "../../Config";
import { GET } from "../../Config/ApiRequests";
import PageHeader from "../../Routes/PageHeader";

const AwbPaymentViewPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchPaymentDetails();
    }
  }, [id]);

  const fetchPaymentDetails = async () => {
    setLoading(true);
    try {
      const response = await GET(`${API.AWB_PAYMENT_ACTIONS}${id}`);
      const data = response?.data || response;
      if (data?.id) {
        setPaymentData(data);
      } else {
        message.error("Failed to load payment details");
      }
    } catch (error) {
      message.error("Failed to load payment details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Payment Details columns
  const detailColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Account",
      key: "account",
      width: "40%",
      render: (_, record) => {
        const accNo = record?.account_no || "-";
        const accName = record?.accounts?.account_name;
        return accName ? `${accNo} - ${accName}` : accNo;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "35%",
      render: (text) => text || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "15%",
      align: "right",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
  ];

  // Ledger Transaction columns
  const ledgerColumns = [
    {
      title: "#",
      key: "index",
      width: 10,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "trans_date",
      key: "trans_date",
      width: "10%",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Trans Type",
      dataIndex: "trans_type",
      key: "trans_type",
      width: "10%",
      render: (v) => v || "-",
    },
    {
      title: "Account",
      key: "account",
      width: "20%",
      render: (_, record) => {
        const accNo = record?.account_no || "-";
        const accName = record?.accounts?.account_name;
        return accName ? `${accNo} - ${accName}` : accNo;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "10%",
      align: "right",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Dr/Cr",
      dataIndex: "dr_or_cr",
      key: "dr_or_cr",
      width: "8%",
      align: "center",
      render: (v) => (
        <Tag color={v === "Dr" ? "green" : "red"}>{v || "-"}</Tag>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference_no",
      key: "reference_no",
      width: "12%",
      render: (text) => text || "-",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: "30%",
      ellipsis: true,
      render: (text) => text || "-",
    },
  ];

  // Voucher columns
  const voucherColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Voucher ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: "Date",
      dataIndex: "voucher_date",
      key: "voucher_date",
      width: "15%",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Type",
      dataIndex: "voucher_type",
      key: "voucher_type",
      width: "10%",
      render: (v) => <Tag color="blue">{v || "-"}</Tag>,
    },
    {
      title: "Account",
      key: "account",
      width: "25%",
      render: (_, record) =>
        record?.accounts
          ? `${record.account_no} - ${record.accounts.account_name}`
          : record?.account_no || "-",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "20%",
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Dr Total",
      dataIndex: "dr_total",
      key: "dr_total",
      width: "10%",
      align: "right",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Cr Total",
      dataIndex: "cr_total",
      key: "cr_total",
      width: "10%",
      align: "right",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
  ];

  // Voucher Details columns (for expandable row)
  const voucherDetailsColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Account",
      key: "account",
      width: "30%",
      render: (_, record) => {
        const accNo = record?.account_no || "-";
        const accName = record?.accounts?.account_name;
        return accName ? `${accNo} - ${accName}` : accNo;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text) => text || "-",
    },
    {
      title: "Dr Amount",
      dataIndex: "amount_dr",
      key: "amount_dr",
      width: "15%",
      align: "right",
      render: (v) => (v != null && v > 0 ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Cr Amount",
      dataIndex: "amount_cr",
      key: "amount_cr",
      width: "15%",
      align: "right",
      render: (v) => (v != null && v > 0 ? Number(v).toFixed(2) : "-"),
    },
  ];

  // Expandable row render for voucher details
  const expandedVoucherRow = (record) => {
    const details = record?.voucherDetails || [];
    return (
      <div style={{ padding: "8px 16px", background: "#fafafa" }}>
        <Table
          columns={voucherDetailsColumns}
          dataSource={details}
          pagination={false}
          size="small"
          rowKey="id"
          locale={{ emptyText: "No voucher details" }}
        />
      </div>
    );
  };

  if (loading) {
    return <LoadingBox />;
  }

  if (!paymentData) {
    return (
      <div>
        <PageHeader breadScrum="Admin / AWB Payment / View" />
        <Card>
          <p>Payment not found</p>
          <Button onClick={() => history.push("/auth/AirwayBillPayment")}>
            Back to Payment List
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader breadScrum="Admin / AWB Payment / View" />
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/auth/AirwayBillPayment")}
            >
              Back
            </Button>
            <span>AWB Payment Details</span>
            {paymentData?.hasVoucher && (
              <Tag icon={<CheckCircleOutlined />} color="success">
                Voucher Created
              </Tag>
            )}
          </div>
        }
      >
        {/* Basic Info */}
        <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1 }}>
          <Descriptions.Item label="ID">{paymentData?.id || "-"}</Descriptions.Item>
          <Descriptions.Item label="Date">
            {paymentData?.date ? moment(paymentData?.date).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="AWB No">
            <strong>{paymentData?.awb?.awb_no || "-"}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Bound">
            <Tag color={paymentData?.bound === "inbound" ? "blue" : "orange"}>
              {paymentData?.bound || "-"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Amount">
            <strong style={{ color: "#1890ff", fontSize: 16 }}>
              {paymentData?.amount != null ? Number(paymentData?.amount).toFixed(2) : "-"}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Reference No">
            {paymentData?.referenceno || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Account No">
            {paymentData?.account_no || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {paymentData?.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            {paymentData?.user?.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {paymentData?.createdat
              ? moment(paymentData?.createdat).format("DD-MM-YYYY HH:mm")
              : "-"}
          </Descriptions.Item>
        </Descriptions>

        {/* Payment Details */}
        <div style={{ marginTop: 24 }}>
          <h3>Payment Details</h3>
          <Table
            columns={detailColumns}
            dataSource={paymentData?.awbPaymentDetails || []}
            pagination={false}
            size="small"
            rowKey="id"
            locale={{ emptyText: "No payment details" }}
          />
        </div>

        {/* Ledger Transactions */}
        <div style={{ marginTop: 24 }}>
          <h3>Ledger Transactions</h3>
          <Table
            columns={ledgerColumns}
            dataSource={paymentData?.ledgerTransactions || []}
            pagination={false}
            size="small"
            rowKey="id"
            locale={{ emptyText: "No ledger transactions" }}
          />
        </div>

        {/* Vouchers Section */}
        {paymentData?.hasVoucher && paymentData?.vouchers?.length > 0 && (
          <>
            <Divider />
            <div style={{ marginTop: 24 }}>
              <h3>
                <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                Payment Vouchers
              </h3>
              <Table
                columns={voucherColumns}
                dataSource={paymentData?.vouchers || []}
                pagination={false}
                size="small"
                rowKey="id"
                locale={{ emptyText: "No vouchers" }}
                expandable={{
                  expandedRowRender: expandedVoucherRow,
                  rowExpandable: (record) => record?.voucherDetails?.length > 0,
                }}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default AwbPaymentViewPage;
